import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// 1. Ant Design
import { Button, Card, Modal, Skeleton, Tag, Tooltip, Typography } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, } from "@ant-design/icons";
// 2. External libraries
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { useNavigate } from "react-router";
// 3. Internal components
import { DataTable } from "@/components/DataTable";
import { Filters } from "@/components/filters";
// 4. Stores
import { getRootStore } from "@/stores/RootStore";
// 5. Types, utils, styles
import { isAdmin } from "@/utils/permissions";
import styles from "@/styles/TariffsPage.module.less";
const { Text } = Typography;
const tariffTypeLabels = {
    ONE_TIME: "Разовый",
    SUBSCRIPTION_AMOUNT: "Подписка (по сумме)",
    SUBSCRIPTION_VISITS: "Подписка (по визитам)",
    EMERGENCY: "Экстренный",
};
const formatRub = (v) => v === null || v === undefined ? "—" : `${Number(v).toFixed(2)} ₽`;
const TariffsPage = observer(() => {
    const { authStore, tariffsStore } = getRootStore();
    const { dataTable, filtersStore, loading } = tariffsStore;
    const navigate = useNavigate();
    const canEdit = isAdmin(authStore.userRole);
    useEffect(() => {
        dataTable.setFilters(filtersStore.values);
        dataTable.loadData();
    }, [filtersStore.values]);
    const handleOpenPair = (advanceId) => {
        navigate(advanceId ? `/tariffs/${advanceId}` : "/tariffs/new");
    };
    const handleDeletePair = (advanceId, name) => {
        Modal.confirm({
            title: "Деактивировать пару тарифов?",
            content: `«${name}» (ADVANCE) и его LATE-двойник будут переведены в isDeleted. Действие можно откатить только через БД.`,
            okText: "Деактивировать",
            okType: "danger",
            cancelText: "Отмена",
            onOk: () => tariffsStore.deletePair(advanceId),
        });
    };
    const columns = [
        {
            title: "Название",
            key: "name",
            dataIndex: ["advance", "name"],
            sorter: true,
            render: (_, record) => (_jsx(Button, { type: "link", onClick: () => handleOpenPair(record.advance.id), children: record.advance.name })),
        },
        {
            title: "Тип",
            key: "type",
            render: (_, record) => (_jsx(Tag, { color: record.advance.type === "ONE_TIME" ? "blue" : "purple", children: tariffTypeLabels[record.advance.type] ?? record.advance.type })),
        },
        {
            title: "ADVANCE ₽/кВт·ч",
            key: "advancePricePerKwh",
            render: (_, record) => formatRub(record.advance.pricePerKwh),
        },
        {
            title: "ADVANCE сбор",
            key: "advanceOneTimeFee",
            render: (_, record) => formatRub(record.advance.oneTimeServiceFee),
        },
        {
            title: "LATE ₽/кВт·ч",
            key: "latePricePerKwh",
            render: (_, record) => record.late ? formatRub(record.late.pricePerKwh) : "—",
        },
        {
            title: "LATE сбор",
            key: "lateOneTimeFee",
            render: (_, record) => record.late ? formatRub(record.late.oneTimeServiceFee) : "—",
        },
        {
            title: "Отсечка (МСК)",
            key: "cutoffHourMsk",
            render: (_, record) => record.advance.cutoffHourMsk !== null &&
                record.advance.cutoffHourMsk !== undefined
                ? `${String(record.advance.cutoffHourMsk).padStart(2, "0")}:00`
                : "—",
        },
        {
            title: "Смещение, дн.",
            key: "cutoffOffsetDays",
            render: (_, record) => record.advance.cutoffOffsetDays !== null &&
                record.advance.cutoffOffsetDays !== undefined
                ? record.advance.cutoffOffsetDays
                : "—",
        },
        {
            title: "Статус",
            key: "isActive",
            render: (_, record) => record.advance.isActive ? (_jsx(Tag, { color: "green", children: "\u0430\u043A\u0442\u0438\u0432\u0435\u043D" })) : (_jsx(Tag, { children: "\u043D\u0435\u0430\u043A\u0442\u0438\u0432\u0435\u043D" })),
        },
        {
            title: "По умолчанию",
            key: "isDefault",
            render: (_, record) => record.advance.isDefault ? _jsx(Tag, { color: "gold", children: "Default" }) : null,
        },
        {
            title: "Действия",
            key: "actions",
            width: 120,
            render: (_, record) => canEdit ? (_jsxs(_Fragment, { children: [_jsx(Tooltip, { title: "\u0420\u0435\u0434\u0430\u043A\u0442\u0438\u0440\u043E\u0432\u0430\u0442\u044C", children: _jsx(Button, { type: "text", icon: _jsx(EditOutlined, {}), size: "small", onClick: () => handleOpenPair(record.advance.id) }) }), _jsx(Tooltip, { title: "\u0414\u0435\u0430\u043A\u0442\u0438\u0432\u0438\u0440\u043E\u0432\u0430\u0442\u044C", children: _jsx(Button, { type: "text", icon: _jsx(DeleteOutlined, {}), danger: true, size: "small", onClick: () => handleDeletePair(record.advance.id, record.advance.name) }) })] })) : null,
        },
    ];
    const dataTableConfig = {
        header: {
            title: "Тарифы (пары ADVANCE + LATE)",
            actions: canEdit ? (_jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: () => handleOpenPair(), children: "\u0421\u043E\u0437\u0434\u0430\u0442\u044C \u0442\u0430\u0440\u0438\u0444" })) : undefined,
            extraContent: (_jsx(Filters, { filtersStore: filtersStore, title: "\u0424\u0438\u043B\u044C\u0442\u0440\u044B \u0438 \u043F\u043E\u0438\u0441\u043A", showClearAll: true, showApplyButton: false })),
        },
        columns,
        rowKey: (record) => record.advance.id,
        scroll: { x: 1500 },
    };
    if (loading) {
        return (_jsx("div", { className: styles.container, children: _jsxs(Card, { children: [_jsx(Skeleton, { active: true, paragraph: { rows: 8 } }), _jsx(Text, { type: "secondary", children: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u0442\u0430\u0440\u0438\u0444\u043E\u0432\u2026" })] }) }));
    }
    return _jsx(DataTable, { store: dataTable, config: dataTableConfig });
});
export default TariffsPage;
